/**
 * Jeu d'icônes maison (charte, doc 06). Traits fins dessinés sur une grille
 * 24×24, monochrome par `currentColor` : l'icône prend donc la couleur du
 * texte qui l'accompagne — encre au repos, Majorelle sur l'entrée active,
 * ton de l'alerte dans un bandeau. Jamais de vert ni de rouge d'interface.
 *
 * Aucune dépendance externe : le trait reste calé sur l'esthétique « filet »
 * plutôt que sur le style d'une librairie tierce.
 */

import type { ReactElement } from 'react';

export type NomIcone =
  | 'accueil'
  | 'clients'
  | 'mandats'
  | 'investir'
  | 'sorties'
  | 'allocations'
  | 'ordres'
  | 'paniers'
  | 'campagnes'
  | 'situation'
  | 'reporting'
  | 'controle'
  | 'import'
  | 'portefeuilles'
  | 'tresorerie'
  | 'instruments'
  | 'profils'
  | 'tranches'
  | 'agences'
  | 'indices'
  | 'audit'
  | 'information'
  | 'gestion'
  | 'refus'
  | 'quitter'
  | 'creer'
  | 'editer'
  | 'fermer'
  | 'ouvrir'
  | 'derouler'
  | 'replier'
  | 'deplier'
  | 'piece'
  | 'actions';

/** Contenu interne de chaque icône, tracé au trait sur une grille 24×24. */
const TRACES: Record<NomIcone, ReactElement> = {
  accueil: (
    <>
      <path d="M3 10.8 12 3.5l9 7.3" />
      <path d="M5.2 9.4V20h13.6V9.4" />
      <path d="M9.8 20v-5.4h4.4V20" />
    </>
  ),
  clients: (
    <>
      <circle cx="8.6" cy="8.2" r="3" />
      <path d="M3.4 19c0-2.9 2.3-5 5.2-5s5.2 2.1 5.2 5" />
      <path d="M15.4 5.6a3 3 0 0 1 0 5.6" />
      <path d="M16.6 14.2c2.3.3 4 2.2 4 4.8" />
    </>
  ),
  mandats: (
    <>
      <path d="M6.5 3.5h6.5L18 8v12.5H6.5z" />
      <path d="M13 3.5V8h5" />
      <path d="M9 12.5h6" />
      <path d="M9 15.8h6" />
      <path d="M9 19h3.5" />
    </>
  ),
  investir: (
    <>
      <path d="M3.5 16.5 9.5 10l3.5 3.5L21 5.5" />
      <path d="M15 5.5h6v6" />
    </>
  ),
  sorties: (
    <>
      <path d="M3.5 7.5 9.5 14l3.5-3.5L21 18.5" />
      <path d="M15 18.5h6v-6" />
    </>
  ),
  allocations: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="4.6" />
      <circle cx="12" cy="12" r="1.2" />
    </>
  ),
  ordres: (
    <>
      <rect x="6" y="4.2" width="12" height="16.3" rx="1.4" />
      <path d="M9 4.2V3h6v1.2" />
      <path d="M9 10h6" />
      <path d="M9 13.6h6" />
      <path d="M9 17.2h4" />
    </>
  ),
  paniers: (
    <>
      <path d="M12 3.3 20 7l-8 3.7L4 7z" />
      <path d="M4 12l8 3.7L20 12" />
      <path d="M4 16.5l8 3.7 8-3.7" />
    </>
  ),
  campagnes: (
    <>
      <path d="M4.5 12a7.5 7.5 0 0 1 12.8-5.3l2.2 2.1" />
      <path d="M19.5 4v4.8h-4.8" />
      <path d="M19.5 12a7.5 7.5 0 0 1-12.8 5.3l-2.2-2.1" />
      <path d="M4.5 20v-4.8h4.8" />
    </>
  ),
  situation: (
    <>
      <path d="M12 3.5v9.5" />
      <path d="M8 9.5l4 4 4-4" />
      <path d="M4.5 16v3a1.5 1.5 0 0 0 1.5 1.5h12a1.5 1.5 0 0 0 1.5-1.5v-3" />
    </>
  ),
  reporting: (
    <>
      <path d="M6.5 3.5h8L18 7v13.5H6.5z" />
      <path d="M14 3.5V7h4" />
      <path d="M9.5 12.5h5" />
      <path d="M9.5 16h5" />
    </>
  ),
  controle: (
    <>
      <path d="M6.5 5.5h11v15h-11z" />
      <path d="M9 5.5V4h6v1.5" />
      <path d="M8.8 11l1.6 1.6 3.2-3.4" />
      <path d="M8.8 16l1.6 1.6 3.2-3.4" />
    </>
  ),
  import: (
    <>
      <path d="M12 3.5v11" />
      <path d="M7.2 10 12 14.7 16.8 10" />
      <path d="M4.5 20h15" />
    </>
  ),
  portefeuilles: (
    <>
      <rect x="3.2" y="7" width="17.6" height="13.5" rx="1.6" />
      <path d="M8.2 7V5.2c0-.8.6-1.4 1.4-1.4h4.8c.8 0 1.4.6 1.4 1.4V7" />
      <path d="M3.2 12.4h17.6" />
    </>
  ),
  tresorerie: (
    <>
      <rect x="3" y="6.5" width="18" height="11" rx="1.5" />
      <circle cx="12" cy="12" r="2.4" />
    </>
  ),
  instruments: (
    <>
      <path d="M8 4v2.6" />
      <rect x="6" y="6.6" width="4" height="6.2" rx="0.6" />
      <path d="M8 12.8v3" />
      <path d="M16 8v2.2" />
      <rect x="14" y="10.2" width="4" height="7.4" rx="0.6" />
      <path d="M16 17.6v2.4" />
    </>
  ),
  profils: (
    <>
      <path d="M12 3.5 19 6v5.3c0 4.4-3 7.4-7 8.9-4-1.5-7-4.5-7-8.9V6z" />
      <circle cx="12" cy="10" r="1.9" />
      <path d="M8.6 15.6c.6-1.7 1.9-2.7 3.4-2.7s2.8 1 3.4 2.7" />
    </>
  ),
  tranches: (
    <>
      <rect x="3.5" y="5" width="17" height="15.5" rx="1.6" />
      <path d="M3.5 9.4h17" />
      <path d="M8 3.2v3.6" />
      <path d="M16 3.2v3.6" />
      <path d="M7.5 13h3M13.5 13h3M7.5 16.6h3M13.5 16.6h3" />
    </>
  ),
  agences: (
    <>
      <path d="M4 20.5V8.4L12 3.5l8 4.9v12.1" />
      <path d="M3 20.5h18" />
      <path d="M9.6 20.5v-4.4h4.8v4.4" />
      <path d="M8 10.4h.01M12 10.4h.01M16 10.4h.01M8 13.4h.01M16 13.4h.01" />
    </>
  ),
  indices: (
    <>
      <path d="M6 3.5v17" />
      <path d="M6 4.5h11l-2.4 3L17 10.5H6z" />
    </>
  ),
  audit: (
    <>
      <path d="M12 3.5 19 6v5.3c0 4.4-3 7.4-7 8.9-4-1.5-7-4.5-7-8.9V6z" />
      <path d="M9 11.4l2.1 2.1 4-4.2" />
    </>
  ),
  information: (
    <>
      <circle cx="12" cy="12" r="8.6" />
      <path d="M12 11v5.2" />
      <path d="M12 7.8h.01" />
    </>
  ),
  gestion: (
    <>
      <path d="M12 4.2 21 19.3H3z" />
      <path d="M12 10v4.2" />
      <path d="M12 17.2h.01" />
    </>
  ),
  refus: (
    <>
      <circle cx="12" cy="12" r="8.6" />
      <path d="M9.2 9.2 14.8 14.8" />
      <path d="M14.8 9.2 9.2 14.8" />
    </>
  ),
  quitter: (
    <>
      <path d="M14 20.5H5.5V3.5H14" />
      <path d="M10 12h10.5" />
      <path d="M17 8.2 20.8 12 17 15.8" />
    </>
  ),
  creer: (
    <>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </>
  ),
  editer: (
    <>
      <path d="M15.5 5 19 8.5 8.5 19 4.5 19.5 5 15.5z" />
      <path d="M13.5 7 17 10.5" />
    </>
  ),
  fermer: (
    <>
      <path d="M6 6 18 18" />
      <path d="M18 6 6 18" />
    </>
  ),
  /** Chevron d'ouverture : suit une référence cliquable, jamais seul. */
  ouvrir: <path d="M9.5 5.5 16 12l-6.5 6.5" />,
  /** Chevron de section : pointe vers le bas quand la section est dépliée. */
  derouler: <path d="M6.5 9.5 12 15l5.5-5.5" />,
  /** Panneau à rail ouvert, flèche vers la gauche : replier le rail. */
  replier: (
    <>
      <rect x="3.5" y="4.5" width="17" height="15" rx="1.6" />
      <path d="M9 4.5v15" />
      <path d="M15.5 9.5 13 12l2.5 2.5" />
    </>
  ),
  /** Pendant du précédent : flèche vers la droite, déplier le rail. */
  deplier: (
    <>
      <rect x="3.5" y="4.5" width="17" height="15" rx="1.6" />
      <path d="M9 4.5v15" />
      <path d="M13 9.5l2.5 2.5-2.5 2.5" />
    </>
  ),
  /** Feuille cornée : accompagne le nom d'une pièce déposée. */
  piece: (
    <>
      <path d="M6.5 3.5h6.5L18 8v12.5H6.5z" />
      <path d="M13 3.5V8h5" />
    </>
  ),
  /** Trois points : ouvre le menu des actions d'une ligne. */
  actions: (
    <>
      <circle cx="5.5" cy="12" r="1.1" fill="currentColor" />
      <circle cx="12" cy="12" r="1.1" fill="currentColor" />
      <circle cx="18.5" cy="12" r="1.1" fill="currentColor" />
    </>
  ),
};

export function Icone({
  nom,
  taille = 18,
  titre,
  classe,
}: {
  nom: NomIcone;
  taille?: number;
  /** Fourni ⇒ l'icône est annoncée ; absent ⇒ décorative (masquée aux AT). */
  titre?: string;
  classe?: string;
}) {
  return (
    <svg
      className={classe}
      width={taille}
      height={taille}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={titre ? 'img' : undefined}
      aria-label={titre}
      aria-hidden={titre ? undefined : true}
      focusable="false"
    >
      {titre ? <title>{titre}</title> : null}
      {TRACES[nom]}
    </svg>
  );
}
